"use client"

import { useState } from "react"
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

import type { ReactNode } from "react"

import { reportCacheError } from "@/components/observability/query-error-reporter"

export function makeQueryClient() {
  return new QueryClient({
    // Central error → telemetry funnel (M21): every query/mutation failure is
    // reported exactly once (deduped by error object), regardless of whether a
    // call site also shows a toast. Per-site onError handlers stay pure UX.
    queryCache: new QueryCache({
      onError: (error, query) =>
        reportCacheError(error, "query", query.queryHash),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        reportCacheError(
          error,
          "mutation",
          JSON.stringify(mutation.options.mutationKey ?? "anonymous")
        ),
    }),
    defaultOptions: {
      queries: {
        staleTime: 30000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  })
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(makeQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
